import { getActiveOrderDrugs } from '../../../../../components/patient-360/overview/orderDrugLinking';
import { humanizeWorkflowStage } from '../../../../../components/nexi/patientEventQueueStatus';

/**
 * One PHOX order that carries a given Rx (PRX-80 Task 13, the "Stage" column).
 *
 * `stage` is the order's WORKFLOW STAGE (`workflowStage`: billing, packaging,
 * ready_to_ship, …) — never `Order.status`, which is the job state
 * (pending / in_progress / completed / failed / cancelled) and rides on the
 * same payload. The first implementation read `status` and its specs passed
 * because the fixtures carried only one of the two; every fixture now carries
 * both. The raw stage is kept verbatim here and only turned into words at
 * render time, so a stage this UI has not heard of still reads as itself.
 */
export interface RxOrderStage {
  orderNumber: string;
  stage: string;
  /** The order's creation instant, ISO — what "most recent" is decided on. */
  createdAt: string | null;
}

const orderTimestamp = (ord: any): string | null => {
  const raw = ord?.createdAt ?? ord?.created_at ?? null;
  return raw == null ? null : String(raw);
};

const byMostRecent = (a: RxOrderStage, b: RxOrderStage): number => {
  // ISO instants sort lexically; a missing timestamp sorts last.
  if (a.createdAt === b.createdAt) return 0;
  if (a.createdAt == null) return 1;
  if (b.createdAt == null) return -1;
  return a.createdAt < b.createdAt ? 1 : -1;
};

const stageOf = (ord: any): RxOrderStage | null => {
  const orderNumber = ord?.orderNumber ?? ord?.order_number;
  const stage = ord?.workflowStage ?? ord?.workflow_stage;
  // An order carrying only a job status has no stage to show — it is skipped, never guessed.
  if (orderNumber == null || stage == null || stage === '') return null;
  return { orderNumber: String(orderNumber), stage: String(stage), createdAt: orderTimestamp(ord) };
};

/**
 * Pure: one pass over the orders, indexing every ACTIVE Rx of every order (the
 * same `getActiveOrderDrugs` linker the Overview tab uses — split-off and
 * removed drugs do not count). Each list is most recent first. The table
 * builds this once per orders payload instead of walking every order for
 * every row on every render.
 */
export function rxOrderStagesByRx(orders: readonly any[] | null | undefined): Map<string, RxOrderStage[]> {
  const byRx = new Map<string, RxOrderStage[]>();
  if (!Array.isArray(orders)) return byRx;
  for (const ord of orders) {
    if (!ord) continue;
    const entry = stageOf(ord);
    if (!entry) continue;
    const { rxNumbers } = getActiveOrderDrugs(ord);
    for (const rx of rxNumbers) {
      const key = String(rx);
      const list = byRx.get(key);
      if (list) list.push(entry);
      else byRx.set(key, [entry]);
    }
  }
  for (const list of byRx.values()) list.sort(byMostRecent);
  return byRx;
}

/**
 * The orders that carry one Rx, most recent first. One Rx can hit several
 * orders, so the caller shows the first entry with a count when there is more
 * than one, and `—` when the list is empty. Nothing here ever picks one silently.
 *
 * No component calls this: the table builds {@link rxOrderStagesByRx} once per
 * orders payload. It is kept, used only by `rxOrderStage.spec.ts`, because it
 * states the single-Rx contract (ordering, skipped orders, the empty case) in
 * one call the spec can pin — and it delegates to the map, so the two cannot
 * disagree.
 */
export function rxOrderStages(
  rxNumber: string | number | null | undefined,
  orders: readonly any[] | null | undefined,
): RxOrderStage[] {
  if (rxNumber == null || rxNumber === '') return [];
  return rxOrderStagesByRx(orders).get(String(rxNumber)) ?? [];
}

/** The cell text for a list from {@link rxOrderStages}: `—`, the stage in words, or the stage with a count. */
export function rxOrderStageLabel(stages: readonly RxOrderStage[]): string {
  if (stages.length === 0) return '—';
  const words = humanizeWorkflowStage(stages[0].stage);
  if (stages.length === 1) return words;
  return `${words} (+${stages.length - 1} more)`;
}
